// # Play history concept

import { fromJS, Map } from 'immutable';
import { createSelector } from 'reselect';
import { get, min, random, isNil } from 'lodash';

import { apiCall } from '../services/api';
import { getRequestTarget } from '../services/response';
import config from '../config';
import TimeRanges from '../constants/TimeRanges';

// # Action Types
const FETCH_TOP_HISTORY = 'history/FETCH_TOP_HISTORY';
const FETCH_TOP_HISTORY_SUCCESS = 'history/FETCH_TOP_HISTORY_SUCCESS';
// const FETCH_TOP_HISTORY_FAIL = 'history/FETCH_TOP_HISTORY_FAIL';

const FETCH_ARTIST_TOP_TRACKS = 'history/FETCH_ARTIST_TOP_TRACKS';
const FETCH_ARTIST_RELATED_ARTISTS = 'history/FETCH_ARTIST_RELATED_ARTISTS';
const SET_TIME_RANGE = 'history/SET_TIME_RANGE';

// # Selectors
export const getTopArtists = state => state.topHistory.get('artists');
export const getTopTracks = state => state.topHistory.get('tracks');
export const getTimeRange = state => state.topHistory.get('timeRange');

export const getTopHistory = createSelector(getTopArtists, getTopTracks, (artists, tracks) =>
  fromJS({ artists, tracks })
);

export const getTopArtistsIds = createSelector(getTopArtists, artists =>
  artists.map(artist => artist.get('id'))
);

export const getTopTracksUris = createSelector(getTopTracks, tracks =>
  tracks.map(track => track.get('uri'))
);

const getFirstImage = target => target.getIn(['images', 0, 'url']);
const getFirstTrackImage = target => target.getIn(['album', 'images', 0, 'url']);
export const getArtistImages = createSelector(getTopArtists, artists => artists.map(getFirstImage));
export const getTrackImages = createSelector(getTopTracks, tracks =>
  tracks.map(getFirstTrackImage)
);

// # Action Creators
export const fetchTop = type => (params = {}) => (dispatch, getState) => {
  const timeRanges = getTimeRange(getState());
  const timeRange = timeRanges.get(type);

  dispatch(
    apiCall({
      type: FETCH_TOP_HISTORY,
      url: `/me/top/${type}`,
      params: Object.assign({}, { limit: 50, time_range: timeRange }, params),
      payload: { target: type },
    })
  );
};

const fetchTopArtists = fetchTop('artists');
const fetchTopTracks = fetchTop('tracks');

export const fetchTopHistory = () => dispatch => {
  return Promise.all([dispatch(fetchTopArtists()), dispatch(fetchTopTracks())]);
};

export const fetchArtistTopTracks = artistId =>
  apiCall({
    type: FETCH_ARTIST_TOP_TRACKS,
    url: `/artists/${artistId}/top-tracks`,
    params: { country: config.DEFAULT_COUNTRY_CODE },
  });

export const fetchRelatedArtists = artistId =>
  apiCall({
    type: FETCH_ARTIST_RELATED_ARTISTS,
    url: `/artists/${artistId}/related-artists`,
  });

export const fetchRecommedations = params =>
  apiCall({
    type: FETCH_ARTIST_RELATED_ARTISTS,
    url: `/recommendations`,
    params,
  });

export const fetchRecommededTracksBasedOnTopArtists = (limit = 10, page = 0) => (
  dispatch,
  getState
) => {
  const artistIds = getTopArtists(getState())
    .slice(page * 5, (page + 1) * 5) // max 5
    .map(artist => artist.get('id'));

  if (!artistIds.size) {
    return Promise.resolve(null);
  }

  return dispatch(fetchRecommedations({ seed_artists: artistIds.join(','), limit }));
};

export const fetchRecommededTracksBasedOnTopTracks = (limit = 10, page = 0) => (
  dispatch,
  getState
) => {
  const trackIds = getTopTracks(getState())
    .slice(page * 5, (page + 1) * 5) // max 5
    .map(artist => artist.get('id'));

  if (!trackIds.size) {
    return Promise.resolve(null);
  }

  return dispatch(fetchRecommedations({ seed_tracks: trackIds.join(','), limit }));
};

export const fetchTopArtistsTopTracks = (count = 20) => (dispatch, getState) => {
  const artistIds = getTopArtistsIds(getState()).slice(0, count);

  if (artistIds.size === 0) {
    return null;
  }

  return Promise.all(artistIds.toJS().map(id => dispatch(fetchArtistTopTracks(id))));
};

export const fetchRelatedArtistsTopTracks = (count = 20) => (dispatch, getState) => {
  const allTopArtists = getTopArtistsIds(getState());
  const artistIds = allTopArtists.slice(0, count).toJS();
  const allTopArtistsIds = allTopArtists.toJS();

  if (artistIds.length === 0) {
    return null;
  }

  return Promise.all(
    artistIds.map(id => {
      // get related artists
      return Promise.resolve(dispatch(fetchRelatedArtists(id))).then(response => {
        // fetch top tracks for first related artist per artist
        //
        // @todo check that related artist isn't include in original artist list
        const relatedArtists = get(response, 'payload.data.artists').map(artist => artist.id);

        // find "fresh" artists, artists that are not in users top artist list already
        const freshRelatedArtists = relatedArtists.filter(id => allTopArtistsIds.indexOf(id) < 0);

        const randomId = random(min([freshRelatedArtists.length, 5])); // random related artist between 1 - 5
        const artistId = get(freshRelatedArtists, [randomId]);

        if (isNil(artistId)) {
          return Promise.resolve(null);
        }

        return dispatch(fetchArtistTopTracks(artistId));
      });
    })
  );
};

export const fetchTopTracksRelatedArtistsTopTracks = (count = 20) => (dispatch, getState) => {
  const allTopArtists = getTopTracks(getState()).map(track => track.getIn(['artists', 0, 'id']));
  const artistIds = allTopArtists.slice(0, count).toJS();
  const allTopArtistsIds = allTopArtists.toJS();

  if (artistIds.length === 0) {
    return null;
  }

  return Promise.all(
    artistIds.map(id => {
      // get related artists
      return Promise.resolve(dispatch(fetchRelatedArtists(id))).then(response => {
        // fetch top tracks for first related artist per artist
        //
        // @todo check that related artist isn't include in original artist list
        const relatedArtists = get(response, 'payload.data.artists').map(artist => artist.id);

        // find "fresh" artists, artists that are not in users top artist list already
        const freshRelatedArtists = relatedArtists.filter(id => allTopArtistsIds.indexOf(id) < 0);

        const randomId = random(min([freshRelatedArtists.length, 5])); // random related artist between 1 - 5
        const artistId = get(freshRelatedArtists, [randomId]);

        if (isNil(artistId)) {
          return Promise.resolve(null);
        }

        return dispatch(fetchArtistTopTracks(artistId));
      });
    })
  );
};

export const setTimeRange = target => timeRange => ({
  type: SET_TIME_RANGE,
  payload: { target, timeRange },
});

export const setArtistsTimeRange = setTimeRange('artists');
export const setTracksTimeRange = setTimeRange('tracks');

// # Reducer
const initialState = fromJS({
  artists: {},
  tracks: {},
  timeRange: {
    artists: TimeRanges.LONG,
    tracks: TimeRanges.LONG,
  },
  isLoading: false,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_TOP_HISTORY: {
      // Clear on fetch
      const target = getRequestTarget(action);
      return state.set(target, Map());
    }

    case FETCH_TOP_HISTORY_SUCCESS: {
      const target = getRequestTarget(action);
      return state.set(target, fromJS(action.payload.data.items));
    }

    case SET_TIME_RANGE: {
      return state.setIn(['timeRange', action.payload.target], action.payload.timeRange);
    }

    default: {
      return state;
    }
  }
}



// WEBPACK FOOTER //
// ./src/concepts/top-history.js